import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { groomingService } from "../../../services";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const buildDiagnosticForm = (assessment, order) => ({
  coat_condition: String(assessment?.coat_condition || ""),
  matting_level: String(assessment?.matting_level || ""),
  nail_condition: String(assessment?.nail_condition || ""),
  skin_condition: String(assessment?.skin_condition || ""),
  behavior_condition: String(assessment?.behavior_condition || ""),
  notes: String(assessment?.notes || ""),
  recommended_work: String(assessment?.recommended_work || ""),
  assigned_to_id: order?.assigned_to?.id ? String(order.assigned_to.id) : "",
});

const hasDiagnosticData = (assessment) => {
  if (!assessment) return false;
  const fields = [
    "coat_condition",
    "matting_level",
    "nail_condition",
    "skin_condition",
    "behavior_condition",
    "notes",
    "recommended_work",
  ];
  return fields.some((field) => String(assessment?.[field] || "").trim().length > 0);
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operacion.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

function SummaryRow({ label, value }) {
  return (
    <Box
      sx={(theme) => ({
        p: 1,
        borderRadius: 1.25,
        bgcolor: theme.palette.action.hover,
      })}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function GroomingDiagnosticDialog({
  open = false,
  branchId = null,
  orderId = null,
  initialOrder = null,
  canManage = false,
  onClose,
  onContinue,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState(buildDiagnosticForm(null, null));
  const [groomers, setGroomers] = useState([]);
  const [loadingGroomers, setLoadingGroomers] = useState(false);

  useEffect(() => {
    if (!open || !orderId || !branchId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const baseOrder =
          initialOrder && Number(initialOrder?.id) === Number(orderId)
            ? initialOrder
            : await groomingService.getOrder(orderId, { branch_id: branchId });
        if (cancelled) return;
        setOrder(baseOrder || null);
        setForm(buildDiagnosticForm(baseOrder?.assessment || null, baseOrder || null));
      } catch (error) {
        if (cancelled) return;
        setOrder(null);
        setForm(buildDiagnosticForm(null, null));
        setErrorMessage(extractErrorMessage(error, "No se pudo cargar la evaluacion diagnostico."));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [branchId, initialOrder, open, orderId]);

  useEffect(() => {
    if (!open || !branchId || !canManage) return;
    let cancelled = false;

    const loadGroomers = async () => {
      setLoadingGroomers(true);
      try {
        const context = await groomingService.getAppointmentContext({ branch_id: branchId });
        if (cancelled) return;
        setGroomers(toArray(context?.groomers));
      } catch {
        if (cancelled) return;
        setGroomers([]);
      } finally {
        if (!cancelled) {
          setLoadingGroomers(false);
        }
      }
    };

    loadGroomers();
    return () => {
      cancelled = true;
    };
  }, [branchId, canManage, open]);

  const assessment = order?.assessment || null;
  const hasExistingDiagnostic = useMemo(() => hasDiagnosticData(assessment), [assessment]);
  const canEditAssessment = canManage;
  const orderUiStatus = String(order?.ui_status || "").toUpperCase();
  const canEditResponsible = canManage && !["COBRADO", "CANCELADO"].includes(orderUiStatus);

  const persistAssignedToIfNeeded = useCallback(
    async (baseOrder) => {
      if (!canEditResponsible || !branchId || !orderId) return baseOrder;

      const selectedAssignedToId = Number.parseInt(String(form.assigned_to_id || "").trim(), 10);
      const normalizedAssignedToId =
        Number.isInteger(selectedAssignedToId) && selectedAssignedToId > 0
          ? selectedAssignedToId
          : null;
      const currentAssignedToId = Number(baseOrder?.assigned_to?.id || 0) || null;
      if (normalizedAssignedToId === currentAssignedToId) return baseOrder;

      const updatedOrder = await groomingService.updateOrder(orderId, {
        branch_id: branchId,
        assigned_to_id: normalizedAssignedToId,
      });
      return updatedOrder || baseOrder;
    },
    [branchId, canEditResponsible, form.assigned_to_id, orderId]
  );

  const handleContinue = useCallback(async () => {
    if (!order) {
      onContinue?.(order);
      return;
    }
    setSaving(true);
    setErrorMessage("");
    try {
      const updatedOrder = await persistAssignedToIfNeeded(order);
      setOrder(updatedOrder || order);
      onContinue?.(updatedOrder || order);
    } catch (error) {
      setErrorMessage(extractErrorMessage(error, "No se pudo actualizar el responsable."));
    } finally {
      setSaving(false);
    }
  }, [onContinue, order, persistAssignedToIfNeeded]);

  const handleSaveAndContinue = useCallback(async () => {
    if (!branchId || !orderId) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const response = await groomingService.saveOrderAssessment(orderId, {
        branch_id: branchId,
        coat_condition: form.coat_condition,
        matting_level: form.matting_level,
        nail_condition: form.nail_condition,
        skin_condition: form.skin_condition,
        behavior_condition: form.behavior_condition,
        notes: form.notes,
        recommended_work: form.recommended_work,
      });
      const orderAfterAssessment =
        response?.order ||
        (await groomingService.getOrder(orderId, { branch_id: branchId }));
      const updatedOrder = await persistAssignedToIfNeeded(orderAfterAssessment || null);
      setOrder(updatedOrder || null);
      onContinue?.(updatedOrder || null);
    } catch (error) {
      setErrorMessage(extractErrorMessage(error, "No se pudo guardar la evaluacion diagnostico."));
    } finally {
      setSaving(false);
    }
  }, [branchId, form, onContinue, orderId, persistAssignedToIfNeeded]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Evaluacion diagnostico</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {loading ? <Typography>Cargando evaluacion...</Typography> : null}
          {!loading && order ? (
            <>
              <Alert severity={canEditAssessment && !hasExistingDiagnostic ? "warning" : "info"}>
                {canEditAssessment
                  ? hasExistingDiagnostic
                    ? "Evaluacion diagnostico registrada. Puedes editarla o continuar sin cambios."
                    : "Evaluacion diagnostico pendiente. Puedes completarla ahora o continuar sin evaluacion."
                  : hasExistingDiagnostic
                    ? "Esta orden ya tiene evaluacion diagnostico registrada."
                    : "Esta orden aun no tiene evaluacion diagnostico registrada."}
              </Alert>

              <TextField
                select
                label="Groomer / responsable"
                size="small"
                value={form.assigned_to_id}
                onChange={(event) => setForm((prev) => ({ ...prev, assigned_to_id: event.target.value }))}
                fullWidth
                disabled={!canEditResponsible || loadingGroomers || saving}
                helperText={
                  !canEditResponsible
                    ? "No editable para ordenes cobradas o canceladas."
                    : loadingGroomers
                      ? "Cargando responsables..."
                      : "Selecciona el responsable de la cita/orden."
                }
              >
                <MenuItem value="">Sin asignar</MenuItem>
                {groomers.map((groomer) => (
                  <MenuItem key={groomer?.id} value={String(groomer?.id || "")}>
                    {groomer?.name || "-"}
                  </MenuItem>
                ))}
              </TextField>

              {!canEditAssessment ? (
                <Box
                  sx={{
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  }}
                >
                  <SummaryRow label="Pelaje" value={assessment?.coat_condition} />
                  <SummaryRow label="Nudos" value={assessment?.matting_level} />
                  <SummaryRow label="Unas" value={assessment?.nail_condition} />
                  <SummaryRow label="Piel" value={assessment?.skin_condition} />
                  <SummaryRow label="Comportamiento" value={assessment?.behavior_condition} />
                  <SummaryRow label="Notas" value={assessment?.notes} />
                  <SummaryRow label="Trabajo recomendado" value={assessment?.recommended_work} />
                </Box>
              ) : (
                <Stack spacing={1.1}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.1}>
                    <TextField
                      label="Pelaje"
                      size="small"
                      fullWidth
                      value={form.coat_condition}
                      onChange={(event) => setForm((prev) => ({ ...prev, coat_condition: event.target.value }))}
                    />
                    <TextField
                      label="Nudos"
                      size="small"
                      fullWidth
                      value={form.matting_level}
                      onChange={(event) => setForm((prev) => ({ ...prev, matting_level: event.target.value }))}
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.1}>
                    <TextField
                      label="Unas"
                      size="small"
                      fullWidth
                      value={form.nail_condition}
                      onChange={(event) => setForm((prev) => ({ ...prev, nail_condition: event.target.value }))}
                    />
                    <TextField
                      label="Piel"
                      size="small"
                      fullWidth
                      value={form.skin_condition}
                      onChange={(event) => setForm((prev) => ({ ...prev, skin_condition: event.target.value }))}
                    />
                  </Stack>
                  <TextField
                    label="Comportamiento"
                    size="small"
                    fullWidth
                    value={form.behavior_condition}
                    onChange={(event) => setForm((prev) => ({ ...prev, behavior_condition: event.target.value }))}
                  />
                  <TextField
                    label="Notas"
                    multiline
                    minRows={3}
                    fullWidth
                    value={form.notes}
                    onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                  />
                  <TextField
                    label="Trabajo recomendado"
                    multiline
                    minRows={3}
                    fullWidth
                    value={form.recommended_work}
                    onChange={(event) => setForm((prev) => ({ ...prev, recommended_work: event.target.value }))}
                  />
                </Stack>
              )}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cerrar
        </Button>
        {canEditAssessment ? (
          <>
            <Button onClick={handleContinue} disabled={loading || saving}>
              Continuar sin guardar
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAndContinue}
              disabled={loading || saving}
            >
              {saving ? "Guardando..." : hasExistingDiagnostic ? "Guardar cambios y continuar" : "Guardar y continuar"}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={loading || saving}
          >
            Continuar al detalle
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default GroomingDiagnosticDialog;
